import type { Agent } from '@studio/models/agent';
import type { Preset } from '@studio/models/control/preset';
import { createSignedUrl } from '#~/api/control/preset/index.js';
import { toIdFromHex } from '#~/api/index.js';
import type { Device } from '#~/device/index.js';
import { StudioError } from '#~/error/index.js';
import { captureThumbnail } from '#~/livekit/index.js';

/**
 * Upload a thumbnail image for the specified preset.
 *
 * @param agentId - The ID of the agent managing the device.
 * @param deviceId - The ID of the device for which a thumbnail is to be uploaded.
 * @param presetId - The ID of the preset.
 * @param token - The token.
 * @returns The public URL of the uploaded thumbnail image.
 */
export const uploadThumbnail = async (
  agentId: Agent['id'],
  deviceId: Device['id'],
  presetId: Preset['id'],
  token: string
): Promise<string> => {
  try {
    const bAgentId = toIdFromHex(agentId);
    const bDeviceId = toIdFromHex(deviceId);
    const bPresetId = toIdFromHex(presetId);

    if (!bAgentId || !bDeviceId || !bPresetId) {
      throw new Error('UT-013');
    }

    // Obtain a thumbnail of the device video
    const thumbnail = await captureThumbnail(deviceId);

    // Obtain the signed upload URL
    const signedUrl = await createSignedUrl(bAgentId, bDeviceId, bPresetId, thumbnail.size, token);

    // Post the image to the signed URL
    const response = await fetch(signedUrl.uploadUrl, {
      body: thumbnail,
      headers: signedUrl.headers,
      method: 'PUT'
    });

    if (response.status !== 200) {
      throw new Error(`UT-816/${response.status}`);
    }

    return `${signedUrl.publicUrl}?${Date.now()}`;
  } catch (cause) {
    throw new StudioError('Unable to upload preset thumbnail.', { cause });
  }
};
